// Чистата логика на панела на рецепта в „Продадени рецепти": дните с продажби
// (и дължината на лентата им спрямо най-силния ден), сборовете на табовете
// „Отчети" и „Съставки" и табовете с ←/→. Нулевите редове
// НЕ се показват никъде — ден без продажби, отчет, в който рецептата е продадена
// и сторнирана (сбор 0), съставка с 0 за периода: тирета без информация.
import dayjs, { type Dayjs } from "dayjs";
import type { SoldDaily } from "../../../../api/inventory";
import type { SoldRecipeIngredient, SoldRecipeReport } from "../../../../api/inventorySoldAnalysis";

export type RecipeTab = "days" | "reports" | "ingredients";
export const RECIPE_TABS: RecipeTab[] = ["days", "reports", "ingredients"];

// ←/→ между табовете, по кръга
export const nextTab = (current: RecipeTab, step: 1 | -1): RecipeTab =>
  RECIPE_TABS[(RECIPE_TABS.indexOf(current) + step + RECIPE_TABS.length) % RECIPE_TABS.length];

export type SoldDayRow = { key: string; day: Dayjs; quantity: number; totalSum: number };

// Под половин стотинка / половин хилядна — нула (сторно до 0,000 не е продажба)
const isZeroSale = (quantity: number, money: number) =>
  Math.abs(quantity) < 0.0005 && Math.abs(money) < 0.005;

// Дните от периода С продажби, по реда на календара, ключ по календарен ден
export const buildDayRows = (from: Dayjs, to: Dayjs, daily: SoldDaily[]): SoldDayRow[] => {
  const byDay = new Map(daily.map((entry) => [dayjs(entry.date).format("YYYY-MM-DD"), entry]));
  const rows: SoldDayRow[] = [];
  for (let day = from.startOf("day"); !day.isAfter(to, "day"); day = day.add(1, "day")) {
    const key = day.format("YYYY-MM-DD");
    const hit = byDay.get(key);
    const quantity = hit?.quantity ?? 0;
    const totalSum = hit?.totalSum ?? 0;
    if (!isZeroSale(quantity, totalSum)) rows.push({ key, day, quantity, totalSum });
  }
  return rows;
};

// Дължината на лентата на всеки ден в „По дни" — делът (0–1) спрямо най-силния
// ден в списъка, в реда на редовете. Отрицателен ден (сторно повече от
// продаденото) и списък без положителен ден → 0.
export const dayBarShares = (rows: SoldDayRow[]): number[] => {
  const peak = rows.reduce((max, row) => Math.max(max, row.quantity), 0);
  return rows.map((row) => (peak > 0 && row.quantity > 0 ? row.quantity / peak : 0));
};

export const nonZeroReports = (reports: SoldRecipeReport[]) =>
  reports.filter((report) => !isZeroSale(report.quantity, report.totalSum));

export const nonZeroIngredients = (ingredients: SoldRecipeIngredient[]) =>
  ingredients.filter((ingredient) => !isZeroSale(ingredient.quantity, ingredient.valueWithVat));

export const sumBy = <T,>(rows: T[], valueOf: (row: T) => number) =>
  rows.reduce((sum, row) => sum + (valueOf(row) || 0), 0);

export const reportsTotals = (reports: SoldRecipeReport[]) => ({
  quantity: sumBy(reports, (report) => report.quantity),
  totalSum: sumBy(reports, (report) => report.totalSum),
});

export const ingredientsTotals = (ingredients: SoldRecipeIngredient[]) => ({
  valueNoVat: sumBy(ingredients, (ingredient) => ingredient.valueNoVat),
  valueWithVat: sumBy(ingredients, (ingredient) => ingredient.valueWithVat),
});

// Ден + кратък ден от седмицата („15.07.2026 ср") — dayjs е без bg locale,
// затова денят от седмицата идва от Intl
export const formatDayWithWeekday = (day: Dayjs) =>
  `${day.format("DD.MM.YYYY")} ${day.toDate().toLocaleDateString("bg-BG", { weekday: "short" })}`;
